/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { Icon, ICON_SIZE } from "../Icon";

export interface IFloatingContainerHeaderProps {
	className?: string;
	hideCloseButton?: boolean;
	onClose?: () => void;
}

export const FloatingContainerHeader: React.FC<IFloatingContainerHeaderProps> = ({
	children,
	className,
	hideCloseButton,
	onClose
}) => {
	const baseClass = BEM("edt-floating-container-header");
	const showCloseButton = useMemo(
		() => !hideCloseButton && isFunction(onClose),
		[ hideCloseButton, onClose ]
	);

	const handleCloseClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
		(event) => {
			if (isFunction(onClose)) {
				event.preventDefault();
				event.stopPropagation();
				onClose();
			}
		},
		[ onClose ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("title").toClassName()}>{children}</div>

			<div className={baseClass.element("fill").toClassName()} />
			{showCloseButton && (
				<div className={baseClass.element("close-btn").toClassName()} onClick={handleCloseClick}>
					<Icon name="mdi/close" size={ICON_SIZE.XSMALL} />
				</div>
			)}
		</div>
	);
};
